"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRecords = void 0;
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const record_1 = require("../model/record");
const context_1 = require("../context");
const use_meta_1 = require("./use_meta");
const model_1 = require("../model");
const store_1 = require("../store");
const core_1 = require("@apitable/core");
const view_computed_1 = require("../view_computed");
/**
 * @internal
 */
function useRecords(param1, param2, param3) {
    const context = (0, react_1.useContext)(context_1.WidgetContext);
    const isDatasheet = param1 instanceof model_1.Datasheet;
    const viewId = isDatasheet ? param2 : param1;
    const query = isDatasheet ? param3 : param2;
    const { datasheetId: metaDatasheetId } = (0, use_meta_1.useMeta)();
    const snapshot = (0, react_redux_1.useSelector)(store_1.getSnapshot);
    const datasheetId = isDatasheet ? param1.datasheetId : metaDatasheetId;
    (0, view_computed_1.useReferenceCount)(datasheetId, viewId);
    const visibleRows = (0, react_redux_1.useSelector)(state => {
        var _a;
        const snapshot = (_a = (0, store_1.getWidgetDatasheet)(state, datasheetId)) === null || _a === void 0 ? void 0 : _a.snapshot;
        if (!datasheetId || !snapshot || !viewId) {
            return null;
        }
        return core_1.Selectors.getVisibleRowsWithoutSearch(state, datasheetId, viewId);
    });
    return (0, react_1.useMemo)(() => {
        let _visibleRows = visibleRows;
        if (!datasheetId || !_visibleRows)
            return [];
        if (query && 'ids' in query) {
            if (!query.ids) {
                return [];
            }
            const idSet = new Set(query.ids);
            _visibleRows = _visibleRows.filter(row => idSet.has(row.recordId));
        }
        // secondary filter
        if (query === null || query === void 0 ? void 0 : query.filter) {
            const { error } = (0, core_1.validateOpenFilter)(query.filter);
            if (error) {
                throw new Error(`filter query validate error: ${error.message}`);
            }
            const state = context.widgetStore.getState();
            const snapshot = (0, store_1.getSnapshot)(state, datasheetId);
            const filterInfo = (0, core_1.parseInnerFilter)(query.filter, { state, fieldMap: snapshot.meta.fieldMap }) || undefined;
            const viewFilterDerivate = new core_1.ViewFilterDerivate(state, datasheetId);
            _visibleRows = viewFilterDerivate.getFilterRowsBase({ filterInfo, rows: _visibleRows, recordMap: snapshot.recordMap });
        }
        return _visibleRows.map(row => new record_1.Record(datasheetId, context, row.recordId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasheetId, visibleRows, query, context, snapshot]);
}
exports.useRecords = useRecords;
